import React, { useState, useEffect } from 'react';
import { Form, ReferenceInput, AutocompleteInput, SelectInput, DateInput, NumberField, useRecordContext, useTranslate } from 'react-admin';
import Box from '@mui/material/Box';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import DateField from '../common/fields/DateField';
import TimeEntryBarChart from '../time_entries/charts/TimeEntryBarChart';
import TimeEntryPieChart from '../time_entries/charts/TimeEntryPieChart';

const optionText = choice => choice?.name ? choice.name : '';
const userOptionText = choice => choice?.fullname ? choice.fullname : '';


const ProjectTimeEntryPieCharts = ({ filters, data }) => {
	return (
		<Box height="200px">
			<TimeEntryPieChart {...filters} groupBy="type" {...(filters?.groupBy === 'type' ? { disableFetch: true, data } : {})} />
			<TimeEntryPieChart {...filters} groupBy="user" {...(filters?.groupBy === 'user' ? { disableFetch: true, data } : {})} />
		</Box>
	);
};

const ProjectTimeEntryChart = () => {
	const record = useRecordContext();
	const translate = useTranslate();
	const [data, setData] = useState();
	const [total, setTotal] = useState();
	const [groupedData, setGroupedData] = useState();
	const [typeId, setTypeId] = useState();
	const [userId, setUserId] = useState();
	const [startDate, setStartDate] = useState();
	const [endDate, setEndDate] = useState();
	const [freq, setFreq] = useState('monthly');
	const [groupBy, setGroupBy] = useState('type');
	const filters = { typeId, userId, freq, groupBy, startDate, endDate };

	const groupByChoices = [
		{ id: 'user', name: translate('User') },
		{ id: 'type', name: translate('Type') }
	];

	useEffect(() => {
		if (data?.length > 0) {
			const grouped = Object.values(
				data.reduce((acc, item) => {
					if (!acc[item.name]) {
						acc[item.name] = { name: item.name, value: 0 };
					}
					acc[item.name].value += item.value;
					return acc;
				}, {})
			).sort((a, b) => b.value - a.value);
			setGroupedData(grouped);
		}
	}, [data]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleChangeType = (id) => {
		setTypeId(id || null);
	};

	const handleChangeUser = (id) => {
		setUserId(id || null);
	};

	const handleChangeStartDate = (e) => {
		setStartDate(e?.target?.value || null);
	};

	const handleChangeEndDate = (e) => {
		setEndDate(e?.target?.value || null);
	};

	const handleChangeFreq = (e, value) => {
		setFreq(value);
	};

	const handleChangeGroupBy = (e) => {
		if (e?.target?.value) {
			setGroupBy(e.target.value);
		}
		else {
			setGroupBy(null);
		}
	};

	return (
		<Box>
			<Form defaultValues={{ type_id: typeId, user_id: userId, start_date: startDate, end_date: endDate, freq, group_by: groupBy }}>
				<Box display="flex" justifyContent="space-between" gap={1}>
					<ReferenceInput source="type_id" reference="time_entry_types" sort={{ field: 'name', order: 'ASC' }}>
						<AutocompleteInput optionText={optionText} size="small" label="Type" onChange={handleChangeType} />
					</ReferenceInput>
					<ReferenceInput source="user_id" reference="users" sort={{ field: 'id', order: 'ASC' }}>
						<AutocompleteInput optionText={userOptionText} size="small" label="User" onChange={handleChangeUser} />
					</ReferenceInput>
					<SelectInput source="group_by" onChange={handleChangeGroupBy} choices={groupByChoices} sx={{ margin: 0 }} />
					<DateInput source="start_date" onChange={handleChangeStartDate} />
					<DateInput source="end_date" onChange={handleChangeEndDate} />
					<ToggleButtonGroup size="small" value={freq} onChange={handleChangeFreq} exclusive sx={{ height: '40px' }}>
						<ToggleButton value="daily">{translate('Daily')}</ToggleButton>
						<ToggleButton value="monthly">{translate('Monthly')}</ToggleButton>
						<ToggleButton value="yearly">{translate('Yearly')}</ToggleButton>
					</ToggleButtonGroup>
				</Box>
			</Form>
			<Box sx={{ width: '100%', height: '400px', paddingBottom: '20px' }}>
				<TimeEntryBarChart
					{...filters}
					projectId={record?.id}
					setData={setData}
					setTotal={setTotal}
					showTotal={false}
				/>
			</Box>
			<Box display="flex" justifyContent="space-between" gap={1}>
				<TableContainer>
					<Table sx={{ minWidth: 650 }}>
						<TableHead>
							<TableRow>
								<TableCell>{translate('Name')}</TableCell>
								<TableCell align="right">{translate('Hours')} (h)</TableCell>
								<TableCell align="right">{translate('Days')} (j)</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{groupedData?.map((item) => (
								<TableRow
									key={item.name}
									sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
								>
									<TableCell component="th" scope="row">
										{!groupBy ? <DateField record={{ value: item.name }} source="value" /> : item.name}
									</TableCell>
									<TableCell align="right">
										<NumberField record={{ value: item.value }} source="value" />
									</TableCell>
									<TableCell align="right">
										<NumberField record={{ value: Math.round((item.value / 8) * 4) / 4 }} source="value" />
									</TableCell>
									<TableCell align="right">
										<NumberField record={{ value: ((item.value / total) * 100)?.toFixed(1) }} source="value" options={{ style: 'unit', unit: 'percent' }} />
									</TableCell>
								</TableRow>
							))}
							<TableRow>
								<TableCell />
								<TableCell align="right">{translate('Total')}: <NumberField record={{ total }} source="total" /></TableCell>
								<TableCell align="right"><NumberField record={{ total: Math.round((total / 8) * 4) / 4 }} source="total" /></TableCell>
								<TableCell />
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
				<Box>
					<ProjectTimeEntryPieCharts filters={filters} data={data} />
				</Box>
			</Box>
		</Box>
	);
};

export default ProjectTimeEntryChart;